import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { Offcanvas, Form, Button, Row, Col } from 'react-bootstrap'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import { CreateUser, searchDepartment, searchDesignation, searchLocation } from '../../services/UserService'
import BarLoader from '../../components/Loader/BarLoader'
import Select from "react-select"
import { roleDisplayMap } from '../../utilities'
import { FaEye, FaEyeSlash, FaPlus } from 'react-icons/fa'
import { searchAreaByDepartmentApi } from '../../services/SearchService'



const userValidationSchema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string().required('Email is required'),
  mobileNumber : Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]+$/, 'Mobile number must be 10 digits')
    .length(10, 'Mobile number must be exactly 10 digits'),
  role : Yup.string().required('Role is required'),  
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),

  location: Yup.string().when("role", {
    is: (role) => role !== "AUDITOR",
    then: (schema) => schema.required('Location is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  designation: Yup.string().when("role", {
    is: (role) => role !== "AUDITOR",
    then: (schema) => schema.required('Designation is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  department: Yup.string().when("role", {
    is: (schema) => schema !== "AUDITOR",
    then: (schema) => schema.required('Department is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  area: Yup.array().when("role", {
    is: (role) => role !== "AUDITOR",
    then: (schema) => 
        schema
        .of(Yup.string().required())
        .min(1, 'At least one area is required'),
    otherwise: (schema) => schema.notRequired()
  })
});

const roleOptions = Object.entries(roleDisplayMap).map(([value, label]) => ({
  value,
  label,
}));

function AddUser({ showOffcanvas, setShowOffcanvas, onUserAdded }) {
  const [creating, setCreating] = useState(false);
  const [isDesignationLoading, setIsDesignationLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
  const [isAreaLoading, setIsAreaLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const {control, handleSubmit, formState: {errors}, reset, getValues, setValue, watch} = useForm({
    resolver: yupResolver(userValidationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      location: '',
      area: '',
      designation: '',
      department: '',
      role: '',
      password: ''
    }
  })

  const selectedRole = watch('role');

  useEffect(() => {
    if (selectedRole) {
        setValue('department', '');
        setValue('area', []);
        setValue('designation', '');
        setValue('location', '');
    }
  }, [selectedRole])

  const fetchDesignation = async () => {
        if (designations.length > 0) return; // Avoid fetching if already populated
        try {
            setIsDesignationLoading(true);
            const response = await searchDesignation('');
            const mapped = response.data.results.map(dept => ({
                label: dept.label,
                value: dept.value
            }))
            setDesignations(mapped);
        }
        catch (error) {
            console.log(error)
        }
        finally {
            setIsDesignationLoading(false);
        }
    };
    

    const fetchLocations = async () => {
        if (locations.length > 0) return; // Avoid fetching if already populated
        try {
            setIsLocationLoading(true);
            const response = await searchLocation('');
            const mapped = response.data.results.map(loaction => ({
                value: loaction.value,
                label: loaction.label
            }))
            setLocations(mapped);
        }
        catch (error) {
            console.log(error)
        }
        finally {
            setIsLocationLoading(false);
        }
    };
    

  const fetchDepartmentsByLocation = async (locationId) => {
      try {
          setIsDepartmentLoading(true);
          const response = await searchDepartment(locationId); // pass locationId if API expects it
          const mapped = response.data.results.map((dept) => ({
          value: dept.value,
          label: dept.label,
          }));
          setDepartments(mapped);
      } catch (error) {
          console.error("Failed to fetch departments:", error);
      }
      finally {
          setIsDepartmentLoading(false);
      }
  };

  const fetchAreaByDepartment = async (departmentId) => {
    try {
        setIsAreaLoading(true);
        const response = await searchAreaByDepartmentApi(departmentId);
        const mapped = response.data.results.map((area) => ({
            value: area.value,
            label: area.label,
        }))
        setAreas(mapped)
    }
    catch (error) {
        console.log("Failed to fetch ares", error)
    }
    finally {
        setIsAreaLoading(false);
    }
  }

  const handleDiscard = () => {
    setShowOffcanvas(false);
    reset();
  }

  const onSubmit = async (data) => {
    try {
        const payload = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            mobile_number: data.mobileNumber,
            location: data.location,
            designation: data.designation,
            department: data.department,
            role: data.role,
            password: data.password,
            area: data.area
        };
        setCreating(true);
        const response = await CreateUser(payload);
        toast.success('User created successfully');
        
        if (onUserAdded) {
            onUserAdded(response.data);
        }
        reset();
        setShowOffcanvas(false);
    }
    catch (error) {
        toast.error(error.response?.data?.error || 'Failed to create user');
    }
    finally {
        setCreating(false);
    }
  }


  return (
    <Offcanvas show={showOffcanvas} onHide={handleDiscard} placement='end' className='w-40'>
        <Offcanvas.Header closeButton className='pb-0'>
            <Offcanvas.Title>
                <span className='d-flex align-items-center gap-2'>
                 <FaPlus size={13}/>
                New User
                </span>
            </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
                
                <Row className='mb-2'>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="firstName">First Name</Form.Label>
                            <Controller
                                name="firstName"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter First Name"
                                        isInvalid={!!errors.firstName}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.firstName?.message && (
                                <div className='text-danger small'>{errors.firstName?.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="lastName">Last Name</Form.Label>
                            <Controller
                                name="lastName"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Last Name"
                                        isInvalid={!!errors.lastName}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.lastName?.message && (
                                <div className='text-danger small'>{errors.lastName?.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row className='mb-2'>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="email">Email</Form.Label>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter email"
                                        isInvalid={!!errors.email}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.email?.message && (
                                <div className='text-danger small'>{errors.email.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="mobileNumber">Mobile Number</Form.Label>
                            <Controller
                                name="mobileNumber"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Mobile Number"
                                        isInvalid={!!errors.mobileNumber}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.mobileNumber?.message && (
                                <div className='text-danger small'>{errors.mobileNumber.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row className='mb-2'>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="role">Role</Form.Label>
                            <Controller
                                name="role"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={roleOptions}
                                        isClearable
                                        placeholder="Select Role"
                                        value={roleOptions.find(option => option.value === field.value)}
                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : "")}
                                        classNamePrefix={"react-select"}
                                    />
                                )}
                            />
                            {errors.role?.message && (
                                <div className='text-danger small'>{errors.role.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                    <Form.Group>
                        <Form.Label>Password</Form.Label>
                        <div className="form-control-wrap">
                            <div className="position-relative">
                                <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        {...field} 
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter Password" 
                                        isInvalid={!!errors.password} 
                                    />
                                )}
                                />
                                <div
                                    className="position-absolute top-50 translate-middle-y"
                                    style={{ right: "18px", cursor: "pointer" }}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    {!errors.password && (!showPassword ? <FaEyeSlash /> : <FaEye />)}
                                </div>
                            </div>
                        </div>
                        {errors.password && <div className="text-danger small">{errors.password.message}</div>}
                    </Form.Group>
                    </Col>
                </Row>

                {selectedRole !== 'AUDITOR' && (
                    <>
                    <Row className='mb-2'>
                        <Col md={6}>
                        <Form.Group>
                            <Form.Label>Designation</Form.Label>
                            <Controller
                            name="designation"
                            control={control}
                            render={({ field }) => (
                                <Select
                                {...field}
                                options={designations}
                                isLoading={isDesignationLoading}
                                placeholder="Select Designation"
                                isClearable
                                value={designations.find(opt => opt.value === field.value)}
                                onChange={(opt) => field.onChange(opt?.value || "")}
                                onFocus={fetchDesignation}
                                classNamePrefix="react-select"
                                />
                            )}
                            />
                            {errors.designation?.message && (
                            <div className='text-danger small'>{errors.designation.message}</div>
                            )}
                        </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group>
                            <Form.Label>Location</Form.Label>
                            <Controller
                            name="location"
                            control={control}
                            render={({ field }) => (
                                <Select
                                {...field}
                                options={locations}
                                isLoading={isLocationLoading}
                                placeholder="Select Location"
                                isClearable
                                value={locations.find(opt => opt.value === field.value)}
                                onMenuOpen={fetchLocations}
                                onChange={(opt) => {
                                    field.onChange(opt?.value || "");
                                    if (!opt) {
                                    setDepartments([]);
                                    setValue('department', "");
                                    setAreas([]);
                                    setValue('area', []);
                                    }
                                }}
                                classNamePrefix="react-select"
                                />
                            )}
                            />
                            {errors.location?.message && (
                            <div className='text-danger small'>{errors.location.message}</div>
                            )}
                        </Form.Group>
                        </Col>
                    </Row>

                    <Col className='mb-2'>
                        <Form.Group>
                        <Form.Label>Department</Form.Label>
                        <Controller
                            name="department"
                            control={control}
                            render={({ field }) => (
                            <Select
                                {...field}
                                options={departments}
                                isLoading={isDepartmentLoading}
                                isClearable
                                value={departments.find(opt => opt.value === field.value)}
                                onMenuOpen={() => {
                                const loc = getValues("location");
                                if (loc) fetchDepartmentsByLocation(loc);
                                }}
                                onChange={(opt) => {
                                field.onChange(opt?.value || "");
                                if (!opt) {
                                    setAreas([]);
                                    setValue("area", []);
                                }
                                }}
                                classNamePrefix="react-select"
                            />
                            )}
                        />
                        {errors.department?.message && (
                            <div className='text-danger small'>{errors.department.message}</div>
                        )}
                        </Form.Group>
                    </Col>

                    <Col>
                        <Form.Group>
                        <Form.Label>Area</Form.Label>
                        <Controller
                            name="area"
                            control={control}
                            render={({ field }) => (
                            <Select
                                {...field}
                                isMulti
                                options={areas}
                                isClearable
                                isLoading={isAreaLoading}
                                value={areas.filter(opt => (field.value || []).includes(opt.value))}
                                onMenuOpen={() => {
                                const dept = getValues("department");
                                if (dept) fetchAreaByDepartment(dept);
                                }}
                                onChange={(opts) => field.onChange(opts ? opts.map(opt => opt.value) : [])}
                                classNamePrefix="react-select"
                            />
                            )}
                        />
                        {errors.area?.message && (
                            <div className='text-danger small'>{errors.area.message}</div>
                        )}
                        </Form.Group>
                    </Col>
                    </>
                )}
                        
                <div className="d-flex justify-content-end mt-4">
                    <Button variant="outline-danger" className="me-2" onClick={handleDiscard}>
                        Discard
                    </Button>
                    <Button type="submit" variant="primary" disabled={creating}>
                        {creating ? <BarLoader/> : 'Submit'}
                    </Button>
                </div>
            </Form>
        </Offcanvas.Body>
    </Offcanvas>
  )
}

export default AddUser